import React, { useState, useEffect } from 'react';
import { Container, Table, Row, Col, Nav, Tab, Button, Card, Spinner, Dropdown, Form, ButtonGroup } from 'react-bootstrap';
import Navbar from '../Navbar';
import PageTitle from '../components/PageTitle';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { 
  getRotationLabel,
  SPECIALTY_ROTATION_COLORS,
  DEFAULT_ROTATION_COLORS,
  getRotationTypesForSpecialty,
  ROTATION_ABBREVIATIONS
} from '../utils/rotationTypesConfig';
import { getWeekStartForDate, getWeeksBetween, getWeeklyGroups } from '../utils/scheduleGenerators/dateHelpers';
import moment from 'moment';

// Helper function to get current academic year
const getCurrentAcademicYear = () => {
  const now = new Date();
  const year = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    year,
    start: `${year}-07-01`,
    end: `${year + 1}-06-30`,
    display: `${year}-${year + 1}`
  };
};

// Helper function to get months between two dates
const getMonthsBetween = (start, end) => {
  if (!start || !end) {
    return [];
  }

  const startDate = new Date(start + "T00:00:00");
  const endDate = new Date(end + "T00:00:00");

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return [];
  }

  const months = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const monthStr = `${year}-${month}`;

    months.push(monthStr);

    // Move to first day of next month
    currentDate = new Date(year, currentDate.getMonth() + 1, 1);
  }

  return months;
};

const ResidentRotationSchedulerPage = () => {
  const [schedules, setSchedules] = useState({});
  const [weekendCallSchedules, setWeekendCallSchedules] = useState({});
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [highlightCurrentUser, setHighlightCurrentUser] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('residentRotationSchedulerTab') || 'rotations';
  });
  const [loading, setLoading] = useState(true);
  const [programDetails, setProgramDetails] = useState(null);
  const [rotationColors, setRotationColors] = useState(DEFAULT_ROTATION_COLORS);
  const [pagerNumbers, setPagerNumbers] = useState({});
  const [profilePicUrls, setProfilePicUrls] = useState({});
  const [residentPagerNumbers, setResidentPagerNumbers] = useState({});
  const [approvedVacationRequests, setApprovedVacationRequests] = useState([]);
  const [fetchingVacationRequests, setFetchingVacationRequests] = useState(false);
  const [offServiceResidents, setOffServiceResidents] = useState([]);
  const [blockPageIndices, setBlockPageIndices] = useState({}); // { pgyLevel: currentPageIndex } for pagination (4 blocks per page)

  // Academic year functionality
  const [viewAcademicYear, setViewAcademicYear] = useState(getCurrentAcademicYear().year);
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);

  // Initialize available academic years
  useEffect(() => {
    const currentYear = getCurrentAcademicYear().year;
    const years = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      years.push(i);
    }
    setAvailableAcademicYears(years);
  }, []);

  // Function to load profile picture with authentication
  const loadProfilePicture = async (profilePicPath) => {
    if (!profilePicPath) return null;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.PROFILE_PICS}${profilePicPath}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        console.warn('Failed to load profile picture:', response.status);
        return null;
      }
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error loading profile picture:', error);
      return null;
    }
  };

  // Function to abbreviate rotation names
  const abbreviateRotation = (rotation) => {
    if (!rotation) return '-';
    
    // Check predefined abbreviations first
    if (ROTATION_ABBREVIATIONS[rotation]) {
      return ROTATION_ABBREVIATIONS[rotation];
    }
    
    // For custom rotations, use smart abbreviation
    const words = rotation.split(' ');
    if (words.length === 1) {
      return rotation.length > 12 ? rotation.substring(0, 10) + '...' : rotation;
    } else {
      // Take first 2-3 letters of each word for better readability
      return words.map(word => word.substring(0, 3)).join(' ').toUpperCase();
    }
  };

  // Function to format phone numbers as (###) ###-####
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digits from input
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    // Format as (###) ###-####
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    }
  };

  // Function to get rotation color with case-insensitive fallback
  const getRotationColor = (rotation) => {
    if (!rotation) return 'transparent';
    
    // Debug: Log available colors on first call
    if (!getRotationColor.debugged) {
      console.log('Available rotation colors:', Object.keys(rotationColors));
      console.log('Program details:', programDetails);
      console.log('Current rotationColors state:', rotationColors);
      getRotationColor.debugged = true;
    }
    
    // Try exact match first
    if (rotationColors[rotation]) {
      return rotationColors[rotation];
    }
    
    // Try case-insensitive match
    const rotationLower = rotation.toLowerCase();
    for (const [key, color] of Object.entries(rotationColors)) {
      if (key.toLowerCase() === rotationLower) {
        console.log(`Case-insensitive match found: "${rotation}" matches "${key}"`);
        return color;
      }
    }
    
    // Try partial match (for variations like "Surgical consultation" vs "Consultation")
    for (const [key, color] of Object.entries(rotationColors)) {
      if (rotationLower.includes(key.toLowerCase()) || key.toLowerCase().includes(rotationLower)) {
        console.log(`Partial match found: "${rotation}" partially matches "${key}"`);
        return color;
      }
    }
    
    console.log(`No color found for rotation: "${rotation}"`);
    return '#6B7280';
  };

  const fetchApprovedVacationRequests = async () => {
    try {
      // Prevent multiple simultaneous calls
      if (fetchingVacationRequests) {
        console.log('Already fetching vacation requests, skipping...');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) return;

      // Get current month for the request
      const months = getAllMonths(schedules);
      const currentMonthStr = months && months[currentMonthIndex];
      if (!currentMonthStr) {
        console.log('No current month found, skipping vacation request fetch');
        return; // Don't reset state if no month found
      }
      
      const [year, month] = currentMonthStr.split('-').map(str => parseInt(str, 10));
      
      console.log('Fetching approved vacation requests for:', { year, month });
      setFetchingVacationRequests(true);
      
      // Declare allRequests variable in the proper scope
      let allRequests = [];
      
      // Try to get all residents' vacation requests - use director endpoint for all users
      // This allows residents to see all vacation requests as requested
      try {
        console.log('Using director endpoint to fetch all residents\' vacation requests (accessible to all users)');
        const response = await axios.get(`${API_ENDPOINTS.DIRECTOR}/vacation-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('Director vacation requests response:', response.data);
        allRequests = response.data || [];
      } catch (directorError) {
        console.log('Director endpoint failed, falling back to resident endpoint:', directorError.message);
        
        // Fallback to resident endpoint (current user only)
        const response = await axios.get(`${API_ENDPOINTS.RESIDENT}/vacation-requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('Resident vacation requests response:', response.data);
        allRequests = response.data.requests || [];
      }
      
      console.log('All vacation requests to process:', allRequests);
      
      try {
        // Filter for approved requests that overlap with the current month
        const monthStart = new Date(year, month - 1, 1);
        const monthEnd = new Date(year, month, 0, 23, 59, 59);
        
        const approvedRequests = allRequests.filter(request => {
          if (request.status !== 'Approved') return false;
          
          const requestStart = new Date(request.startDate);
          const requestEnd = new Date(request.endDate);
          
          // Check if the request overlaps with the current month
          return requestStart <= monthEnd && requestEnd >= monthStart;
        });
        
        console.log('Filtered approved requests for current month:', approvedRequests);
        console.log('Debug filtering details:');
        console.log('- Current month being filtered:', { year, month });
        console.log('- Month start:', monthStart);
        console.log('- Month end:', monthEnd);
        console.log('- All requests with their dates:');
        allRequests.forEach((request, index) => {
          const requestStart = new Date(request.startDate);
          const requestEnd = new Date(request.endDate);
          const overlaps = requestStart <= monthEnd && requestEnd >= monthStart;
          console.log(`  Request ${index + 1}: ${request.startDate} to ${request.endDate} (overlaps: ${overlaps}, status: ${request.status})`);
          console.log(`    Request data:`, request);
        });
        
        // Add resident names - check if names are already included or need to be resolved
        const approvedRequestsWithNames = approvedRequests.map(request => {
          // If the request already has residentName (from new endpoint), use it
          if (request.residentName) {
            return {
              ...request,
              resident: {
                firstName: request.residentName.split(' ')[0] || 'Unknown',
                lastName: request.residentName.split(' ').slice(1).join(' ') || 'Resident'
              }
            };
          }
          
          // Otherwise, find the resident in the schedule data (fallback for old endpoint)
          let residentName = 'Unknown Resident';
          Object.values(schedules).forEach(pgyData => {
            if (pgyData && pgyData.schedule) {
              Object.entries(pgyData.schedule).forEach(([residentId, residentData]) => {
                if (parseInt(residentId) === request.residentId && residentData.name) {
                  residentName = residentData.name;
                }
              });
            }
          });
          
          return {
            ...request,
            resident: {
              firstName: residentName.split(' ')[0] || 'Unknown',
              lastName: residentName.split(' ').slice(1).join(' ') || 'Resident'
            }
          };
        });
        
        console.log('Approved requests with names:', approvedRequestsWithNames);
        setApprovedVacationRequests(approvedRequestsWithNames);
      } catch (apiError) {
        console.log('Error processing vacation requests:', apiError.message);
        setApprovedVacationRequests([]);
      }
    } catch (error) {
      console.error('Error fetching vacation requests:', error);
      setApprovedVacationRequests([]);
    } finally {
      setFetchingVacationRequests(false);
    }
  };

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to view schedules');
        return;
      }

      // Fetch rotation schedules
      const rotationResponse = await axios.get(`${API_ENDPOINTS.SCHEDULES}/rotations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { academicYear: viewAcademicYear }
      });
      setSchedules(rotationResponse.data);

      // Fetch weekend call schedules
      const weekendResponse = await axios.get(`${API_ENDPOINTS.SCHEDULES}/weekend-call`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { academicYear: viewAcademicYear }
      });
      setWeekendCallSchedules(weekendResponse.data);

      // Fetch pager numbers
      try {
        const pagerResponse = await axios.get(`${API_ENDPOINTS.SCHEDULES}/pager-numbers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPagerNumbers(pagerResponse.data);
      } catch (error) {
        console.error('Error fetching pager numbers:', error);
        // Continue without pager numbers if they fail to load
      }

      // For residents, we can only get pager numbers from the current user
      // and any other residents that might be accessible through other means
      try {
        // Get current user data which should include pager number
        const currentUserResponse = await axios.get(`${API_ENDPOINTS.AUTH}/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        

        
        // Extract pager numbers - start with current user
        const pagerNumbers = {};
        if (currentUserResponse.data.pagerNumber) {
          pagerNumbers[currentUserResponse.data.id] = currentUserResponse.data.pagerNumber;
        }
        
        // Try to get other residents from the users endpoint (without pager numbers)
        try {
          const usersResponse = await axios.get(`${API_ENDPOINTS.USERS}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Note: The users endpoint doesn't include pager numbers
          // So we can only show pager numbers for the current user
          
        } catch (usersError) {
          console.error('Error fetching users:', usersError);
        }
        

        setResidentPagerNumbers(pagerNumbers);
      } catch (error) {
        console.error('Error fetching resident pager numbers:', error);
        // Continue without resident pager numbers if they fail to load
      }

      // Update current month index based on fetched schedules
      const months = getAllMonths(rotationResponse.data);
      if (months.length > 0) {
        // Find the current month in the available months
        const currentDate = new Date();
        const currentMonthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        const currentMonthIndex = months.findIndex(month => month >= currentMonthStr);
        
        // If current month is found, use it; otherwise use the last available month
        if (currentMonthIndex !== -1) {
          setCurrentMonthIndex(currentMonthIndex);
        } else {
          // If current month is not in schedule, use the last available month
          setCurrentMonthIndex(months.length - 1);
        }
      } else {
        setCurrentMonthIndex(-1);
      }

      // Fetch approved vacation requests
      await fetchApprovedVacationRequests();

      // Fetch off-service residents
      await fetchOffServiceResidents();

    } catch (error) {
      console.error('Error fetching schedules:', error);
      setError('Failed to load schedules. Please try again later.');
    }
  };

  // Fetch off-service residents
  const fetchOffServiceResidents = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_ENDPOINTS.SCHEDULES}/off-service-residents`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { academicYear: viewAcademicYear }
      });
      setOffServiceResidents(response.data || []);
    } catch (error) {
      console.error('Error fetching off-service residents:', error);
    }
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Please log in to view schedules');
          return;
        }

        const response = await axios.get(`${API_ENDPOINTS.AUTH}/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUser(response.data);
      } catch (error) {
        console.error('Error fetching current user:', error);
        setError('Failed to load user data. Please try again later.');
      }
    };

    const fetchProgramDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // Use resident-accessible endpoint
        const res = await fetch(`${API_ENDPOINTS.PROGRAMS}/my-program-details`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          // Endpoint returns { success, id, name, description, specialty, maxPGYLevel }
          setProgramDetails(data);
          
          // Update rotation colors based on program specialty
          const specialty = data.specialty || data?.program?.specialty;
          if (specialty) {
            const { colors } = getRotationTypesForSpecialty(specialty);
            setRotationColors(colors);
            // After setting specialty colors, fetch custom rotation types to merge their colors
            fetchAllRotationTypes(colors, specialty);
          } else {
            // Even without specialty, try to fetch custom rotation types
            fetchAllRotationTypes(null, null);
          }
        }
      } catch (err) {
        console.error("Failed to fetch program details", err);
      }
    };

    // Fetch all rotation types (standard + custom) from database
    const fetchAllRotationTypes = async (specialtyColors = null, specialty = null) => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('No token available for fetching rotation types');
          return;
        }
        
        console.log('Fetching rotation types from API...');
        const response = await axios.get(`${API_ENDPOINTS.CUSTOM_ROTATION_TYPES}/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('Rotation types API response:', {
          rotationTypes: response.data.rotationTypes?.length,
          rotationColors: Object.keys(response.data.rotationColors || {}).length,
          customRotationTypes: response.data.customRotationTypes?.length
        });
        console.log('API rotation colors:', response.data.rotationColors);
        
        // Merge rotation colors instead of overwriting - preserve specialty colors
        // Only add custom rotation colors, don't overwrite specialty colors
        setRotationColors(prev => {
          const apiColors = response.data.rotationColors || {};
          // Use specialtyColors if provided, otherwise use prev
          const baseColors = specialtyColors || prev;
          console.log('Merging colors - baseColors:', Object.keys(baseColors).length, 'apiColors:', Object.keys(apiColors).length);
          
          // Preserve all existing colors (including specialty colors), only add new ones from API
          const merged = { ...baseColors };
          Object.keys(apiColors).forEach(key => {
            // Always add custom rotation colors from API - they should override defaults but not specialty colors
            // If it's a specialty color, preserve it; otherwise use the API color
            if (specialty && SPECIALTY_ROTATION_COLORS[specialty]?.[key]) {
              // Keep specialty color, don't override
              console.log(`Preserving specialty color for "${key}": ${SPECIALTY_ROTATION_COLORS[specialty][key]}`);
            } else {
              // Use API color (could be custom or standard)
              merged[key] = apiColors[key];
              console.log(`Setting color for "${key}": ${apiColors[key]}`);
            }
          });
          console.log('Final merged rotation colors:', merged);
          console.log('Merged rotation colors keys:', Object.keys(merged));
          return merged;
        });
        
      } catch (error) {
        console.error('Error fetching rotation types:', error);
        if (error.response) {
          console.error('API Error Response:', error.response.status, error.response.data);
        }
        // Don't throw - this is not critical for the page to function
      }
    };

    const initializeData = async () => {
      try {
        await Promise.all([fetchSchedules(), fetchCurrentUser(), fetchProgramDetails()]);
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
  }, []);

  // Refetch schedules when viewAcademicYear changes
  useEffect(() => {
    if (!loading) {
      fetchSchedules();
    }
  }, [viewAcademicYear]);

  // Fetch vacation requests when month changes
  useEffect(() => {
    if (schedules && Object.keys(schedules).length > 0 && !loading) {
      console.log('useEffect triggered: fetching vacation requests for month index:', currentMonthIndex);
      fetchApprovedVacationRequests();
    }
  }, [currentMonthIndex, schedules, loading]);

  // Helper function to get all months from schedules
  const getAllMonths = (schedules) => {
    const allMonths = new Set();
    Object.values(schedules).forEach((scheduleData) => {
      // Get months from the schedule's date range
      if (scheduleData.startDate && scheduleData.endDate) {
        const months = getMonthsBetween(scheduleData.startDate, scheduleData.endDate);
        months.forEach(month => allMonths.add(month));
      }
    });
    return Array.from(allMonths).sort();
  };

// Function to get weekends for a specific month
  const getWeekendsForMonth = (yearMonth) => {
    if (!yearMonth) return [];
    
    const [year, month] = yearMonth.split('-');
    const weekends = [];
    const date = new Date(year, month - 1, 1);
    
    while (date.getMonth() === month - 1) {
      if (date.getDay() === 6) { // Saturday
        const saturday = new Date(date);
        const sunday = new Date(date);
        sunday.setDate(sunday.getDate() + 1);
        
        // Format dates as YYYY-MM-DD in local time to avoid timezone issues
        const saturdayStr = `${saturday.getFullYear()}-${String(saturday.getMonth() + 1).padStart(2, "0")}-${String(saturday.getDate()).padStart(2, "0")}`;
        const sundayStr = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, "0")}-${String(sunday.getDate()).padStart(2, "0")}`;
        
        weekends.push({
          saturday: saturdayStr,
          sunday: sundayStr
        });
      }
      date.setDate(date.getDate() + 1);
    }
    return weekends;
  };

  // Function to get all dates between start and end dates
  const getAllDates = () => {
    const allDates = new Set();
    Object.values(schedules).forEach(scheduleData => {
      const [startYear, startMonth, startDay] = scheduleData.startDate.split('-').map(Number);
      const [endYear, endMonth, endDay] = scheduleData.endDate.split('-').map(Number);
      
      const start = new Date(startYear, startMonth - 1, startDay);
      const end = new Date(endYear, endMonth - 1, endDay);
      const current = new Date(start);
      
      while (current <= end) {
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        allDates.add(dateStr);
        current.setDate(current.getDate() + 1);
      }
    });
    return Array.from(allDates).sort();
  };

  // Function to handle date navigation
  const handleDateNavigation = (direction) => {
    const allDates = getAllDates();
    const currentDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    const currentIndex = allDates.indexOf(currentDateStr);

    if (direction === 'next' && currentIndex < allDates.length - 1) {
      const [year, month, day] = allDates[currentIndex + 1].split('-').map(Number);
      setCurrentDate(new Date(year, month - 1, day));
    } else if (direction === 'prev' && currentIndex > 0) {
      const [year, month, day] = allDates[currentIndex - 1].split('-').map(Number);
      setCurrentDate(new Date(year, month - 1, day));
    }
  };

  // Function to handle month navigation
  const handleMonthNavigation = (direction) => {
    const months = getAllMonths(schedules);
    if (months.length === 0) return;

    if (direction === 'next' && currentMonthIndex < months.length - 1) {
      setCurrentMonthIndex(prev => prev + 1);
    } else if (direction === 'prev' && currentMonthIndex > 0) {
      setCurrentMonthIndex(prev => prev - 1);
    }
  };

  // Function to get residents for a specific date and rotation
  const getResidentsForDate = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const residentsByRotation = {};

    // Get all unique rotations from the rotation schedule
    const allRotations = new Set();
    Object.values(schedules).forEach(scheduleData => {
      if (!scheduleData.schedule) return;
      const isWeekly = scheduleData.scheduleType === 'weekly';
      const isBlocks = scheduleData.scheduleType === 'blocks';
      Object.values(scheduleData.schedule).forEach(residentData => {
        if (!residentData.rotations) return;
        if (isWeekly || isBlocks) {
          const weekKey = getWeekStartForDate(date);
          const rotation = residentData.rotations[weekKey];
          if (rotation) allRotations.add(rotation);
        } else {
          const rotation = residentData.rotations[monthStr];
          if (rotation) allRotations.add(rotation);
        }
      });
    });

    // Add rotations from off-service residents
    offServiceResidents.forEach(osr => {
      const osrMonth = (osr.month || '').trim();
      if (osrMonth === monthStr && osr.rotationName) {
        allRotations.add(osr.rotationName.trim());
      }
    });

    // Initialize rotations
    Array.from(allRotations).forEach(rotation => {
      residentsByRotation[rotation] = [];
    });

    // Get residents for each rotation
    Object.entries(schedules).forEach(([pgyLevel, scheduleData]) => {
      if (!scheduleData.schedule) return;
      const isWeekly = scheduleData.scheduleType === 'weekly';
      const isBlocks = scheduleData.scheduleType === 'blocks';
      Object.entries(scheduleData.schedule).forEach(([residentId, residentData]) => {
        if (!residentData.rotations) return;
        const rotation = (isWeekly || isBlocks)
          ? residentData.rotations[getWeekStartForDate(date)]
          : residentData.rotations[monthStr];
        if (rotation) {
          if (!residentsByRotation[rotation].some(r => r.id === residentId)) {
            residentsByRotation[rotation].push({
              id: residentId,
              name: residentData.name,
              year: residentData.year
            });
          }
        }
      });
    });

    // Add off-service residents for this date
    offServiceResidents.forEach(osr => {
      const osrMonth = (osr.month || '').trim();
      const osrRotation = (osr.rotationName || '').trim();
      
      // Check if this off-service resident matches the date's month
      if (osrMonth === monthStr && osrRotation) {
        // Initialize rotation if it doesn't exist
        if (!residentsByRotation[osrRotation]) {
          residentsByRotation[osrRotation] = [];
        }
        
        // Check if this off-service resident is already added
        const offServiceId = `off-service-${osr.id}`;
        if (!residentsByRotation[osrRotation].some((r) => r.id === offServiceId)) {
          residentsByRotation[osrRotation].push({
            id: offServiceId,
            name: osr.residentName,
            year: osr.pgyLevel,
            isOffService: true
          });
        }
      }
    });

    // For Saturday and Sunday, add weekend call residents
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    if (dayOfWeek === 0 || dayOfWeek === 6) { // Sunday or Saturday
      const weekendSchedules = getCombinedWeekendSchedules();
      
      Object.entries(weekendSchedules).forEach(([rotation, weekendData]) => {
        // Check if this rotation has weekend call data for this specific date
        if (weekendData[dateStr]) {
          const weekendResidents = weekendData[dateStr].residents || [];
          
          // If this rotation doesn't exist in residentsByRotation, create it
          if (!residentsByRotation[rotation]) {
            residentsByRotation[rotation] = [];
          }
          
          // Add weekend call residents to the rotation
          weekendResidents.forEach((weekendResident) => {
            // Check if resident is already in the list
            const existingResident = residentsByRotation[rotation].find(
              (r) => r.id === weekendResident.id
            );
            
            if (!existingResident) {
              residentsByRotation[rotation].push({
                id: weekendResident.id,
                name: weekendResident.name,
                year: weekendResident.year,
                isOnCall: true, // Mark as on call
              });
            } else {
              // If resident already exists, mark them as on call
              existingResident.isOnCall = true;
            }
          });
        }
      });
    }

    return residentsByRotation;
  };

  // Function to get combined weekend schedules
  const getCombinedWeekendSchedules = () => {
    if (!weekendCallSchedules || Object.keys(weekendCallSchedules).length === 0) {
      return {};
    }

    const months = getAllMonths(schedules);
    if (!months || months.length === 0) {
      return {};
    }

    const currentMonth = months[currentMonthIndex];
    if (!currentMonth) {
      return {};
    }

    const weekends = getWeekendsForMonth(currentMonth);
    
    // Get active rotations for the current month
    const activeRotations = new Set();
    Object.values(schedules).forEach(scheduleData => {
      if (!scheduleData.schedule) return;
      const isWeekly = scheduleData.scheduleType === 'weekly';
      const isBlocks = scheduleData.scheduleType === 'blocks';
      Object.values(scheduleData.schedule).forEach(residentData => {
        if (!residentData.rotations) return;
        if (isWeekly || isBlocks) {
          // For weekly/block schedules, check all week keys in this month
          const [year, month] = currentMonth.split('-').map(Number);
          const monthStart = new Date(year, month - 1, 1);
          const monthEnd = new Date(year, month, 0);
          
          // Check each week in the month
          for (let day = 1; day <= monthEnd.getDate(); day++) {
            const checkDate = new Date(year, month - 1, day);
            const weekKey = getWeekStartForDate(checkDate);
            const weekRotation = residentData.rotations[weekKey];
            if (weekRotation) {
              activeRotations.add(weekRotation);
            }
          }
        } else {
          // Monthly schedule
        if (residentData.rotations[currentMonth]) {
          activeRotations.add(residentData.rotations[currentMonth]);
          }
        }
      });
    });
    
    // Create a new object to store the filtered schedules
    const filteredSchedules = {};
    
    // Only process active rotations
    Object.entries(weekendCallSchedules)
      .filter(([rotation]) => activeRotations.has(rotation))
      .forEach(([rotation, weekendData]) => {
        filteredSchedules[rotation] = {};
        
        // Include all weekends for the current month
        weekends.forEach(weekend => {
          // Check if we have data for this weekend
          const saturdayData = weekendData[weekend.saturday];
          const sundayData = weekendData[weekend.sunday];

          if (saturdayData) {
            filteredSchedules[rotation][weekend.saturday] = {
              date: weekend.saturday,
              residents: saturdayData.residents,
              type: 'Saturday'
            };
          }

          if (sundayData) {
            filteredSchedules[rotation][weekend.sunday] = {
              date: weekend.sunday,
              residents: sundayData.residents,
              type: 'Sunday'
            };
          }
        });
      });

    return filteredSchedules;
  };

  // Update localStorage when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('residentRotationSchedulerTab', tab);
  };

  // Add a function to check if a resident is the current user
  const isCurrentUser = (residentId) => {
    return currentUser && String(residentId) === String(currentUser.id);
  };

  // Add a function to render resident name with highlighting
  const renderResidentName = (residentId, residentName) => {
    return (
      <div className="d-flex align-items-center">
        {capitalizeName(residentName)}
        {highlightCurrentUser && isCurrentUser(residentId) && (
          <div className="ring-container">
            <div className="ringring"></div>
            <div className="circle"></div>
          </div>
        )}
      </div>
    );
  };

  // Add a function to render resident name with profile picture (for rotations tab only)
  const renderResidentNameWithPic = (residentId, residentName, profilePic) => {
    if (profilePic) {
      console.log(`Rendering resident ${residentId} (${residentName}) with profilePic: ${profilePic}`);
    }
    
    // Create default avatar function similar to UsersPage
    const defaultAvatar = (name) => {
      const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
      return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' fill='%23e9ecef'/%3E%3Ctext x='16' y='20' font-family='Arial' font-size='14' fill='%236c757d' text-anchor='middle'%3E${initials}%3C/text%3E%3C/svg%3E`;
    };

    // Helper function to capitalize names
    const capitalizeName = (name) => {
      return name.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    return (
      <div className="d-flex align-items-center gap-2">
        <img 
          src={profilePicUrls[residentId] || defaultAvatar(residentName)} 
          alt="avatar" 
          style={{width:32,height:32,borderRadius:'50%'}} 
        />
        <span>{capitalizeName(residentName)}</span>
        {highlightCurrentUser && isCurrentUser(residentId) && (
          <div className="ring-container">
            <div className="ringring"></div>
            <div className="circle"></div>
          </div>
        )}
      </div>
    );
  };

  // Render daily schedule
  const renderDailySchedule = () => {
    const allDates = getAllDates();
    
    if (allDates.length === 0) {
      return (
        <div className="text-center py-4">
          <h5>No schedule dates available</h5>
          <p className="text-muted">Please check back later for updates</p>
        </div>
      );
    }

    const currentDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    if (!allDates.includes(currentDateStr)) {
      const [year, month, day] = allDates[0].split('-').map(Number);
      const newDate = new Date(year, month - 1, day);
      setCurrentDate(newDate);
      return null;
    }

    const residentsByRotation = getResidentsForDate(currentDate);
    const currentIndex = allDates.indexOf(currentDateStr);

    return (
      <Container>


        <Card className="shadow-sm border-0 p-0">
          <Card.Header className="border-0 p-4 d-flex justify-content-between align-items-center border-bottom" style={{backgroundColor: 'var(--bs-body-bg)'}}>


          <div>
            <h5 className="fw-semibold">Daily Schedule</h5>
            {(currentDate.getDay() === 0 || currentDate.getDay() === 6) && (
              <small className="text-muted">
                <i className="bi bi-telephone-fill me-1"></i>
                Weekend call assignments shown for Saturday and Sunday
              </small>
            )}
          </div>
          <div className="d-flex align-items-center gap-3">
            
            <ButtonGroup className="custom-btn-group">
            <Button 
              variant="secondary"
              onClick={() => handleDateNavigation('prev')}
              disabled={currentIndex <= 0}
              size="sm"
            >
              <i className="bi bi-chevron-left"></i>
            </Button>
            <Button disabled size="sm" variant="secondary">
              {window.innerWidth <= 768
                ? currentDate.toLocaleDateString(undefined, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })
                : currentDate.toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
            </Button>
            <Button 
              variant="secondary"
              onClick={() => handleDateNavigation('next')}
              disabled={currentIndex >= allDates.length - 1}
              size="sm"
            >
              <i className="bi bi-chevron-right"></i>
            </Button>
            </ButtonGroup>
          </div>

          </Card.Header>
          <Card.Body className="p-0">
        {Object.keys(residentsByRotation).length === 0 ? (
          
          <div className="text-center">
            <h5>No rotations scheduled for this date</h5>
            <p className="text-muted">Please check the rotation schedule for this month</p>
          </div>
        ) : (
          <Table responsive striped>
            <thead>
              <tr>
                <th className="ps-4">Rotation</th>
                <th>Residents</th>
                <th>Attendings</th>
                <th>APPs</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(residentsByRotation).map(([rotation, residents]) => (
                <tr key={rotation}>
                  <td className="ps-4">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span 
                          className="d-none d-md-inline text-nowrap"
                          style={{
                            backgroundColor: getRotationColor(rotation),
                            color: 'white',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}
                        >
                          {abbreviateRotation(rotation)}
                        </span>
                        <span 
                          className="d-inline d-md-none text-nowrap"
                          style={{
                            backgroundColor: rotationColors[rotation] || '#6B7280',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}
                        >
                          {getRotationLabel(rotation)}
                        </span>
                        {pagerNumbers[rotation] && (
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="light"
                              size="sm"
                              className="py-1 px-2 no-arrow" 
                              style={{ color: 'inherit' }}
                            >
                              <i className="bi bi-telephone" style={{ fontSize: '1rem' }}></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.ItemText>
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="me-3 text-nowrap">{formatPhoneNumber(pagerNumbers[rotation])}</span>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-1"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      navigator.clipboard.writeText(formatPhoneNumber(pagerNumbers[rotation]));
                                      // Show a brief success message
                                      const button = event.currentTarget;
                                      const icon = button.querySelector('i');
                                      if (icon) {
                                        icon.className = 'bi bi-check';
                                        button.className = 'btn link-success btn-sm p-1';
                                        setTimeout(() => {
                                          icon.className = 'bi bi-clipboard';
                                          button.className = 'btn link-primary btn-sm p-1';
                                        }, 1000);
                                      }
                                    }}
                                  >
                                    <i className="bi bi-clipboard"></i>
                                  </Button>
                                </div>
                              </Dropdown.ItemText>
                            </Dropdown.Menu>
                          </Dropdown>
                        )}
                      </div>
                      {(currentDate.getDay() === 0 || currentDate.getDay() === 6) && (
                        <div className="small text-muted mt-1">
                          {residents.filter(r => r.isOnCall).length} on call
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {residents.length === 0 ? (
                      <div className="text-muted">No residents assigned</div>
                    ) : (
                      residents.map((resident, index) => {
                        // Check if this is the current user
                        const isCurrentUser = currentUser && currentUser.id === resident.id;
                        
                        return (
                          <div key={`${resident.id}-${index}`}>
                            <span className="d-flex align-items-center gap-2">
                              {renderResidentName(resident.id, `${resident.name} (PGY-${resident.year})`)}
                              {/* Pager numbers are only shown for the current user in resident view for privacy */}
                              {residentPagerNumbers[resident.id] && (
                                <Dropdown>
                                  <Dropdown.Toggle
                                    variant="light"
                                    size="sm"
                                    className="py-1 px-2 no-arrow"
                                    style={{ color: 'inherit' }}
                                  >
                                    <i className="bi bi-telephone" style={{ fontSize: '0.8rem' }}></i>
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.ItemText>
                                      <div className="d-flex align-items-center justify-content-between">
                                        <span className="me-3 text-nowrap">{formatPhoneNumber(residentPagerNumbers[resident.id])}</span>
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-1"
                                          onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            navigator.clipboard.writeText(formatPhoneNumber(residentPagerNumbers[resident.id]));
                                            // Show a brief success message
                                            const button = event.currentTarget;
                                            const icon = button.querySelector('i');
                                            if (icon) {
                                              icon.className = 'bi bi-check';
                                              button.className = 'btn link-success btn-sm p-1';
                                              setTimeout(() => {
                                                icon.className = 'bi bi-clipboard';
                                                button.className = 'btn link-primary btn-sm p-1';
                                              }, 1000);
                                            }
                                          }}
                                        >
                                          <i className="bi bi-clipboard"></i>
                                        </Button>
                                      </div>
                                    </Dropdown.ItemText>
                                  </Dropdown.Menu>
                                </Dropdown>
                              )}
                              {resident.isOnCall && (
                                <span className="badge bg-danger" style={{ fontSize: '0.7rem', animation: 'pulse 2s infinite' }}>
                                  <i className="bi bi-telephone-fill me-1"></i>
                                  On Call
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        </Card.Body>
        </Card>
      </Container>
    );
  };

  // Helper function to capitalize names
  const capitalizeName = (name) => {
    return name.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="bg-main min-vh-100">
        <PageTitle title="Loading..." />
        <Navbar pageTitle="Rotation Schedule" />

        <div className="bg-white shadow-sm ">
          <Container>
            <Row>
              <Col md={12}>
                <h6 className="mb-0 fw-semibold py-3">Schedules</h6>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner 
              animation="border" 
              variant="primary" 
              size="lg"
              className="mb-3"
            />
            <div className="text-muted">Loading rotation schedules...</div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-main min-vh-100">
      <PageTitle 
        title="Resident Rotation Scheduler" 
        description="Manage and view resident rotation schedules and weekend call assignments"
      />
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <Navbar pageTitle="Rotation Schedule" />

      <div className="bg-white shadow-sm custom-sticky-top">
        <Container>
          <Row>
            <Col lg={3} md={4} sm={6} className="d-flex col-6 order-first order-lg-1 order-md-1 order-sm-1  mt-1 mt-md-0 align-items-center justify-content-start gap-3">
              <h6 className="mb-0 fw-semibold">Schedules</h6>
              <div className="d-flex align-items-center" title="View Academic Year">
                <Form.Select
                  size="sm"
                  value={viewAcademicYear}
                  onChange={(e) => setViewAcademicYear(parseInt(e.target.value))}
                  style={{ width: 'auto' }}
                >
                  {availableAcademicYears.map((year) => (
                    <option key={`view-${year}`} value={year}>
                      {year}-{year + 1}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
            <Col lg={6} md={12} sm={12} className="order-last order-lg-2 order-md-3 order-sm-3">
                    <Nav variant="tabs" style={{marginBottom: '-1px'}} onSelect={handleTabChange}>
          <Nav.Item>
            <Nav.Link eventKey="rotations" active={activeTab === 'rotations'}>Rotations</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="weekend-call" active={activeTab === 'weekend-call'}>Weekend Call</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="daily-schedule" active={activeTab === 'daily-schedule'}>Daily Schedule</Nav.Link>
          </Nav.Item>
        </Nav>
            </Col>
            <Col lg={3} md={6} sm={6} className="d-flex col-6 order-lg-3 order-md-2 order-sm-2 mt-1 mt-md-0 align-items-center justify-content-end">

              {currentUser && (
                <Button
                  variant={highlightCurrentUser ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => setHighlightCurrentUser(!highlightCurrentUser)}
                >
                  <i className="bi bi-person-fill me-1"></i>
                  {highlightCurrentUser ? 'Hide me' : 'Find Me'}
                </Button>
              )}
              {error && (
                <div className="alert alert-danger mb-0" role="alert">
                  {error}
        </div>
              )}  
             
            
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="mt-4">


        <Tab.Content>
          <Tab.Pane eventKey="rotations" active={activeTab === 'rotations'}>
              {Object.keys(schedules).length === 0 ? (
                <Card className="mb-4 shadow-sm border-0 py-4">
                <Card.Body className="p-0">
                <div className="text-center py-5">
                  <div className="d-flex flex-column align-items-center gap-2">
                    <img src="/images/undraw_schedule_6t8k.svg" alt="No rotation schedule" className="img-fluid" style={{height: "200px"}} />
                    <h5>No Rotation Schedules</h5>
                    <p className="text-muted">
                      No rotation schedules have been created yet. Please check back later for updates.
                    </p>
                  </div>
                </div>
                </Card.Body>
                </Card>
              ) : (
                <>
                {(() => {
                  // Group schedules by type
                  const schedulesByType = {
                    monthly: [],
                    weekly: [],
                    blocks: [],
                  };

                  Object.entries(schedules).forEach(([pgyLevel, scheduleData]) => {
                    const scheduleType = scheduleData?.scheduleType || 'monthly';
                    schedulesByType[scheduleType].push({ pgyLevel, scheduleData });
                  });

                  return (
                    <>
                      {/* Monthly Schedules */}
                      {schedulesByType.monthly.length > 0 && schedulesByType.monthly.map(({ pgyLevel, scheduleData }) => (
                        <Card key={`monthly-${pgyLevel}`} className="mb-3 shadow-sm border-0" style={{ borderLeft: '4px solid #0d6efd' }}>
                          <Card.Header className="p-4 border-bottom" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                            <h5 className="fw-semibold">PGY-{pgyLevel} Monthly Schedule</h5>
                </Card.Header>
                <Card.Body className="p-0">
                <Table responsive className="modern-table">
                  <thead>
                    <tr className="custom-header-bg">
                      <th className="ps-4 border-0">Resident</th>
                                  {getMonthsBetween(scheduleData.startDate, scheduleData.endDate).map((monthStr) => {
                          const [year, monthNum] = monthStr.split('-');
                          const date = new Date(year, parseInt(monthNum) - 1);
                          const monthName = date.toLocaleString('default', { month: 'short' });
                          return (
                                      <th key={`month-${monthStr}`} className="py-3 border-0 text-center">
                                  {`${monthName} ${year}`}
                            </th>
                          );
                                  })}
                    </tr>
                  </thead>
                  <tbody>
                        <tr className="pgy-header-row">
                                  <td colSpan={getMonthsBetween(scheduleData.startDate, scheduleData.endDate).length + 1} className="ps-3 border-0"></td>
                        </tr>
                                {Object.entries(scheduleData.schedule || {})
                          .filter(([_, residentData]) => residentData.year?.toString() === pgyLevel)
                          .map(([residentId, residentData]) => (
                            <tr key={`resident-${residentId}`} className="resident-row">
                                      <td className="ps-4 border-0">{renderResidentNameWithPic(residentId, residentData.name, residentData.profilePic)}</td>
                                      {getMonthsBetween(scheduleData.startDate, scheduleData.endDate).map((monthStr) => {
                                        const rotation = residentData.rotations?.[monthStr] || null;
                                        return (
                                          <td key={`${residentId}-${monthStr}`} className="rotation-cell border-0" style={{ padding: '1px 1px' }}>
                                            <div className="rotation-content" style={{
                                              backgroundColor: getRotationColor(rotation),
                                              color: rotation ? 'white' : '#6B7280',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '0.875rem',
                                        borderRadius: '6px',
                                        minHeight: '50px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                            }}>
                                              {abbreviateRotation(rotation) || '-'}
                                  </div>
                                </td>
                                        );
                                      })}
                            </tr>
                          ))}
                                  {/* Off-service residents row */}
                        {(() => {
                                    const pgyOffServiceResidents = offServiceResidents.filter(osr => 
                                      osr.pgyLevel.toString() === pgyLevel.toString() && 
                                      (osr.scheduleType === 'monthly' || !osr.scheduleType)
                                    );
                          if (pgyOffServiceResidents.length === 0) return null;

                          const residentsByMonth = {};
                          pgyOffServiceResidents.forEach(osr => {
                            const month = (osr.month || '').trim();
                            if (!residentsByMonth[month]) {
                              residentsByMonth[month] = [];
                            }
                            residentsByMonth[month].push(osr);
                          });

                          return (
                            <tr key={`off-service-pgy-${pgyLevel}`} className="off-service-resident-row">
                              <td className="ps-4 border-0">
                                          <span className="fw-bold">Off Service</span>
                              </td>
                              {getMonthsBetween(scheduleData.startDate, scheduleData.endDate).map((monthKey) => {
                                const monthResidents = residentsByMonth[monthKey] || [];
                                if (monthResidents.length === 0) {
                                  return (
                                    <td key={`off-service-${pgyLevel}-${monthKey}`} className="rotation-cell border-0" style={{ padding: '1px 1px' }}>
                                      <div className="rotation-content off-service-empty" style={{ minHeight: '50px' }}></div>
                                    </td>
                                  );
                                }

                                const byRotation = {};
                                monthResidents.forEach(osr => {
                                  const rot = (osr.rotationName || '').trim();
                                  if (!byRotation[rot]) {
                                    byRotation[rot] = [];
                                  }
                                  byRotation[rot].push(osr);
                                });

                                const rotationEntries = Object.entries(byRotation);
                                const baseColor = rotationEntries.length > 0 
                                  ? (getRotationColor(rotationEntries[0][0]) || '#6B7280')
                                  : '#6B7280';

                                return (
                                  <td
                                    key={`off-service-${pgyLevel}-${monthKey}`}
                                    className="rotation-cell border-0"
                                    style={{ padding: '1px 1px' }}
                                  >
                                    <div
                                      className="rotation-content"
                                      style={{
                                        minHeight: '50px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        justifyContent: 'center',
                                        padding: '2px',
                                        gap: '2px'
                                      }}
                                    >
                                                {rotationEntries.map(([rotation, residents]) => {
                                        const rotationColor = getRotationColor(rotation) || baseColor;
                                        return (
                                          <div 
                                            key={rotation} 
                                            style={{ 
                                              backgroundColor: rotationColor,
                                              color: 'white',
                                              textAlign: 'center',
                                              fontWeight: '600',
                                              fontSize: '0.875rem',
                                              width: '100%',
                                                        boxSizing: 'border-box',
                                                        minHeight: '50px',
                                                        borderRadius: '6px',
                                                        padding: '6px 4px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexDirection: 'column',
                                                      }}
                                                    >
                                                      <span>{abbreviateRotation(rotation)}</span>
                                                      <div className="badge p-1" style={{ fontSize: '0.65rem', backgroundColor: '#00102020'}}>
                                                        {residents.map(r => r.residentName).join(', ')}
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    );
                                  })()}
                              </tbody>
                            </Table>
                          </Card.Body>
                        </Card>
                      ))}

                      {/* Weekly Schedules */}
                      {schedulesByType.weekly.length > 0 && schedulesByType.weekly.map(({ pgyLevel, scheduleData }) => {
                        const weekDuration = scheduleData?.weekDuration || 1;
                        const groups = getWeeklyGroups(scheduleData.startDate, scheduleData.endDate, weekDuration);
                        return (
                          <Card key={`weekly-${pgyLevel}`} className="mb-3 shadow-sm border-0" style={{ borderLeft: '4px solid #198754' }}>
                            <Card.Header className="p-4 border-bottom" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                              <h5 className="fw-semibold">PGY-{pgyLevel} Weekly Schedule</h5>
                            </Card.Header>
                            <Card.Body className="p-0">
                              <Table responsive className="modern-table">
                                <thead>
                                  <tr className="custom-header-bg">
                                    <th className="ps-4 border-0">Resident</th>
                                    {groups.map((group) => {
                                      const startDateObj = new Date(group.start + 'T00:00:00');
                                      const endDateObj = new Date(group.end + 'T00:00:00');
                                      const label = `${startDateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${endDateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                                      return (
                                        <th key={`week-${group.start}`} className="py-3 border-0 text-center">
                                          {label}
                                        </th>
                                      );
                                    })}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="pgy-header-row">
                                    <td colSpan={groups.length + 1} className="ps-3 border-0"></td>
                                  </tr>
                                  {Object.entries(scheduleData.schedule || {})
                                    .filter(([_, residentData]) => residentData.year?.toString() === pgyLevel)
                                    .map(([residentId, residentData]) => (
                                      <tr key={`resident-${residentId}`} className="resident-row">
                                        <td className="ps-4 border-0">{renderResidentNameWithPic(residentId, residentData.name, residentData.profilePic)}</td>
                                        {groups.map((group) => {
                                          const weekKeys = group.weeks.map((w) => w.start);
                                          const groupRotation = weekKeys.map((wk) => residentData.rotations?.[wk]).find((r) => r) || null;
                                          return (
                                            <td key={`${residentId}-${group.start}`} className="rotation-cell border-0" style={{ padding: '1px 1px' }}>
                                              <div className="rotation-content" style={{
                                                backgroundColor: getRotationColor(groupRotation),
                                                color: groupRotation ? 'white' : '#6B7280',
                                                textAlign: 'center',
                                                fontWeight: '600',
                                                fontSize: '0.875rem',
                                                borderRadius: '6px',
                                                minHeight: '50px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                              }}>
                                                {abbreviateRotation(groupRotation) || '-'}
                                              </div>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                    {/* Off-service residents row */}
                                    {(() => {
                                      const pgyOffServiceResidents = offServiceResidents.filter(osr => 
                                        osr.pgyLevel.toString() === pgyLevel.toString() && 
                                        (osr.scheduleType === 'weekly' || (!osr.scheduleType && !osr.month && !osr.blockIndex))
                                      );
                                      if (pgyOffServiceResidents.length === 0) return null;

                                      const residentsByWeekBlock = {};
                                      pgyOffServiceResidents.forEach(osr => {
                                        const weekBlock = (osr.weekBlock || '').trim();
                                        if (!residentsByWeekBlock[weekBlock]) {
                                          residentsByWeekBlock[weekBlock] = [];
                                        }
                                        residentsByWeekBlock[weekBlock].push(osr);
                                      });

                                      return (
                                        <tr key={`off-service-pgy-${pgyLevel}`} className="off-service-resident-row">
                                          <td className="ps-4 border-0">
                                            <span className="fw-bold">Off Service</span>
                                          </td>
                                          {groups.map((group) => {
                                            const weekBlockResidents = residentsByWeekBlock[group.start] || [];
                                            if (weekBlockResidents.length === 0) {
                                              return (
                                                <td key={`off-service-${pgyLevel}-${group.start}`} className="rotation-cell border-0" style={{ padding: '1px 1px' }}>
                                                  <div className="rotation-content off-service-empty" style={{ minHeight: '50px' }}></div>
                                                </td>
                                              );
                                            }

                                            const byRotation = {};
                                            weekBlockResidents.forEach(osr => {
                                              const rot = (osr.rotationName || '').trim();
                                              if (!byRotation[rot]) {
                                                byRotation[rot] = [];
                                              }
                                              byRotation[rot].push(osr);
                                            });

                                            const rotationEntries = Object.entries(byRotation);
                                            const baseColor = rotationEntries.length > 0 
                                              ? (getRotationColor(rotationEntries[0][0]) || '#6B7280')
                                              : '#6B7280';

                                            return (
                                              <td
                                                key={`off-service-${pgyLevel}-${group.start}`}
                                                className="rotation-cell border-0"
                                                style={{ padding: '1px 1px' }}
                                              >
                                                <div
                                                  className="rotation-content"
                                                  style={{
                                                    
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'stretch',
                                                    justifyContent: 'center',
                                                    padding: '2px',
                                                    gap: '2px'
                                                  }}
                                                >
                                                  {rotationEntries.map(([rotation, residents]) => {
                                                    const rotationColor = getRotationColor(rotation) || baseColor;
                                                    return (
                                                      <div 
                                                        key={rotation} 
                                                        style={{ 
                                                          backgroundColor: rotationColor,
                                                          color: 'white',
                                                          textAlign: 'center',
                                                          fontWeight: '600',
                                                          fontSize: '0.875rem',
                                                          width: '100%',
                                                          boxSizing: 'border-box',
                                                          minHeight: '50px',
                                                          borderRadius: '6px',
                                                          padding: '6px 4px',
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          flexDirection: 'column',
                                                        }}
                                                      >
                                                        <span>{abbreviateRotation(rotation)}</span>
                                            <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                                              {residents.map(r => r.residentName).join(', ')}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })()}
                                </tbody>
                              </Table>
                            </Card.Body>
                          </Card>
                        );
                      })}

                      {/* Block Schedules */}
                      {schedulesByType.blocks.length > 0 && schedulesByType.blocks.map(({ pgyLevel, scheduleData }) => {
                        const blockPattern = scheduleData?.blockPattern || '4-1';
                        const [majorWeeks, minorWeeks] = blockPattern.split('-').map(Number);
                        const blockDuration = scheduleData?.blockDuration || 5;
                        const weeks = getWeeksBetween(scheduleData.startDate, scheduleData.endDate);
                        const totalBlocks = Math.ceil(weeks.length / blockDuration);
                        const currentPageIndex = blockPageIndices[pgyLevel] || 0;
                        const blocksPerPage = 4;
                        const totalPages = Math.ceil(totalBlocks / blocksPerPage);
                        const startBlock = currentPageIndex * blocksPerPage + 1;
                        const endBlock = Math.min((currentPageIndex + 1) * blocksPerPage, totalBlocks);

                        const allBlocks = [];
                        for (let i = 0; i < weeks.length; i += blockDuration) {
                          const blockWeeks = weeks.slice(i, i + blockDuration);
                          if (blockWeeks.length > 0) {
                            const weekStart = Math.floor((i / blockDuration) * blockDuration) + 1;
                            const weekEnd = Math.min(weekStart + blockDuration - 1, weeks.length);
                            allBlocks.push({
                              index: Math.floor(i / blockDuration) + 1,
                              start: blockWeeks[0].start,
                              end: blockWeeks[blockWeeks.length - 1].end,
                              weeks: blockWeeks,
                              majorWeeks: blockWeeks.slice(0, majorWeeks),
                              minorWeeks: blockWeeks.slice(majorWeeks, majorWeeks + minorWeeks),
                              label: `Block ${Math.floor(i / blockDuration) + 1} (Weeks ${weekStart}-${weekEnd})`
                            });
                          }
                        }

                        const visibleBlocks = (() => {
                          const startIndex = currentPageIndex * blocksPerPage;
                          const endIndex = Math.min(startIndex + blocksPerPage, allBlocks.length);
                          return allBlocks.slice(startIndex, endIndex);
                        })();

                        // Get all unique rotations from the schedule to show in legend
                        const allRotations = new Set();
                        Object.values(scheduleData.schedule || {}).forEach(residentData => {
                          if (residentData.rotations) {
                            Object.values(residentData.rotations).forEach(rotation => {
                              if (rotation) allRotations.add(rotation);
                            });
                          }
                        });

                        return (
                          <Card key={`blocks-${pgyLevel}`} className="mb-3 shadow-sm border-0" style={{ borderLeft: '4px solid #dc3545' }}>
                            <Card.Header className="p-4 border-bottom" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                              <div>
                                <h5 className="fw-semibold">PGY-{pgyLevel} Block Schedule</h5>
                                {blockPattern && (
                                  <div className="text-muted small mt-1">
                                    {majorWeeks}-{minorWeeks} Block Schedule Format ({majorWeeks} weeks major rotation + {minorWeeks} week{minorWeeks !== 1 ? 's' : ''} lighter rotation)
                                  </div>
                                )}
                              </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                              <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                                <div className="d-flex align-items-center gap-2">
                                  <i className="bi bi-calendar3"></i>
                                  <span>
                                    Viewing Blocks {startBlock} - {endBlock} of {totalBlocks}
                                  </span>
                                </div>
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                      setBlockPageIndices(prev => ({
                                        ...prev,
                                        [pgyLevel]: Math.max(0, (prev[pgyLevel] || 0) - 1)
                                      }));
                                    }}
                                    disabled={currentPageIndex === 0}
                                  >
                                    <i className="bi bi-chevron-left me-1"></i> <span className="d-none d-md-inline">Previous</span>
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                      setBlockPageIndices(prev => ({
                                        ...prev,
                                        [pgyLevel]: Math.min(totalPages - 1, (prev[pgyLevel] || 0) + 1)
                                      }));
                                    }}
                                    disabled={currentPageIndex >= totalPages - 1}
                                  >
                                    <span className="d-none d-md-inline">Next</span> <i className="bi bi-chevron-right ms-1"></i>
                                  </Button>
                                </div>
                              </div>
                              <Row>
                                <Col md={10} sm={12} className="pe-md-0" style={{ overflowX: 'auto' }}>
                                  <Table responsive className="modern-table">
                                    <thead>
                                      <tr className="custom-header-bg">
                                        <th className="ps-4 py-3 border-0">Resident</th>
                                        {visibleBlocks.map((block) => {
                                          const startDate = moment(block.start, 'YYYY-MM-DD', true).format('D MMM');
                                          const endDate = moment(block.end, 'YYYY-MM-DD', true).format('D MMM');
                                          const dateRange = `${startDate} - ${endDate}`;
                                          return (
                                            <th key={`block-${block.start}`} className="py-3 border-0 text-center">
                                              <div>{block.label}</div>
                                              <div className="small text-muted mt-1">{dateRange}</div>
                                            </th>
                                          );
                                        })}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr className="pgy-header-row">
                                        <td colSpan={visibleBlocks.length + 1} className="ps-3 border-0"></td>
                                      </tr>
                                      {Object.entries(scheduleData.schedule || {})
                                        .filter(([_, residentData]) => residentData.year?.toString() === pgyLevel)
                                        .map(([residentId, residentData]) => (
                                          <tr key={`resident-${residentId}`} className="resident-row">
                                            <td className="ps-4 border-0">{renderResidentNameWithPic(residentId, residentData.name, residentData.profilePic)}</td>
                                            {visibleBlocks.map((block) => {
                                          // Group major weeks by rotation
                                          const majorRotationGroups = [];
                                          let currentRotation = null;
                                          let currentGroupStart = 0;
                                          
                                          block.majorWeeks.forEach((week, idx) => {
                                            const rotation = residentData.rotations?.[week.start];
                                            if (rotation !== currentRotation) {
                                              if (currentRotation !== null && idx > 0) {
                                                majorRotationGroups.push({
                                                  rotation: currentRotation,
                                                  startIdx: currentGroupStart,
                                                  endIdx: idx - 1,
                                                  weeks: idx - currentGroupStart
                                                });
                                              }
                                              currentRotation = rotation;
                                              currentGroupStart = idx;
                                            }
                                          });
                                          if (currentRotation !== null) {
                                            majorRotationGroups.push({
                                              rotation: currentRotation,
                                              startIdx: currentGroupStart,
                                              endIdx: block.majorWeeks.length - 1,
                                              weeks: block.majorWeeks.length - currentGroupStart
                                            });
                                          }
                                          
                                          // Group minor weeks by rotation
                                          const minorRotationGroups = [];
                                          currentRotation = null;
                                          currentGroupStart = 0;
                                          
                                          block.minorWeeks.forEach((week, idx) => {
                                            const rotation = residentData.rotations?.[week.start];
                                            if (rotation !== currentRotation) {
                                              if (currentRotation !== null && idx > 0) {
                                                minorRotationGroups.push({
                                                  rotation: currentRotation,
                                                  startIdx: currentGroupStart,
                                                  endIdx: idx - 1,
                                                  weeks: idx - currentGroupStart
                                                });
                                              }
                                              currentRotation = rotation;
                                              currentGroupStart = idx;
                                            }
                                          });
                                          if (currentRotation !== null) {
                                            minorRotationGroups.push({
                                              rotation: currentRotation,
                                              startIdx: currentGroupStart,
                                              endIdx: block.minorWeeks.length - 1,
                                              weeks: block.minorWeeks.length - currentGroupStart
                                            });
                                          }
                                          
                                          const totalWeeks = majorWeeks + minorWeeks;
                                          
                                          return (
                                            <td
                                              key={`${residentId}-block-${block.start}`}
                                              className="rotation-cell border-0"
                                              style={{ padding: '2px' }}
                                            >
                                              <div style={{ display: 'flex', flexDirection: 'row', gap: '2px', minHeight: '50px', width: '100%' }}>
                                                {/* Major rotation boxes */}
                                                {majorRotationGroups.length > 0 ? (
                                                  majorRotationGroups.map((group, groupIdx) => {
                                                    const rotationColor = getRotationColor(group.rotation) || 'transparent';
                                                    const groupWidthPercent = (group.weeks / totalWeeks) * 100;
                                                    return (
                                                      <div
                                                        key={`major-${groupIdx}`}
                                                        style={{
                                                          backgroundColor: rotationColor,
                                                          color: group.rotation ? 'white' : '#6B7280',
                                                          textAlign: 'center',
                                                          fontWeight: '600',
                                                          fontSize: '0.75rem',
                                                          borderRadius: '6px',
                                                          padding: '6px 4px',
                                                          minHeight: '40px',
                                                          width: `${groupWidthPercent}%`,
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          flexDirection: 'column',
                                                          gap: '2px',
                                                        }}
                                                      >
                                                        <span>{group.rotation ? abbreviateRotation(group.rotation) : '-'}</span>
                                                        <span className="badge py-1 px-2" style={{ fontSize: '0.65rem', backgroundColor: '#00102020' }}>{group.weeks}w</span>
                                                      </div>
                                                    );
                                                  })
                                                ) : (
                                                  <div style={{
                                                    border: '2px dashed var(--border-bg)',
                                                    fontSize: '0.75rem',
                                                    borderRadius: '6px',
                                                    padding: '6px 4px',
                                                    minHeight: '40px',
                                                    width: `${(majorWeeks / totalWeeks) * 100}%`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                  }}>
                                                    <span>-</span>
                                                  </div>
                                                )}
                                                
                                                {/* Minor rotation boxes */}
                                                {minorRotationGroups.length > 0 ? (
                                                  minorRotationGroups.map((group, groupIdx) => {
                                                    const rotationColor = getRotationColor(group.rotation) || 'transparent';
                                                    const groupWidthPercent = (group.weeks / totalWeeks) * 100;
                                                    return (
                                                      <div
                                                        key={`minor-${groupIdx}`}
                                                        style={{
                                                          backgroundColor: rotationColor,
                                                          color: group.rotation ? 'white' : '#6B7280',
                                                          textAlign: 'center',
                                                          fontWeight: '600',
                                                          fontSize: '0.75rem',
                                                          borderRadius: '6px',
                                                          padding: '6px 4px',
                                                          minHeight: '50px',
                                                          width: `${groupWidthPercent}%`,
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          justifyContent: 'center',
                                                          flexDirection: 'column',
                                                          gap: '2px',
                                                        }}
                                                      >
                                                        <span>{group.rotation ? abbreviateRotation(group.rotation) : '-'}</span>
                                                        <span className="badge py-1 px-2" style={{ fontSize: '0.65rem', backgroundColor: '#00102020' }}>{group.weeks}w</span>
                                                      </div>
                                                    );
                                                  })
                                                ) : (
                                                  <div style={{
                                                    border: '2px dashed var(--border-bg)',
                                                    color: '#6B7280',
                                                    fontSize: '0.75rem',
                                                    borderRadius: '6px',
                                                    padding: '6px 4px',
                                                    minHeight: '40px',
                                                    width: `${(minorWeeks / totalWeeks) * 100}%`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                  }}>
                                                    <span>-</span>
                                                  </div>
                                                )}
                                              </div>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                    {/* Off-service residents row */}
                                    {(() => {
                                      const pgyOffServiceResidents = offServiceResidents.filter(osr => 
                                        osr.pgyLevel.toString() === pgyLevel.toString() && 
                                        (osr.scheduleType === 'blocks' || (!osr.scheduleType && !osr.month && !osr.weekBlock))
                                      );
                                      if (pgyOffServiceResidents.length === 0) return null;

                                      const residentsByBlock = {};
                                      pgyOffServiceResidents.forEach(osr => {
                                        const blockIndex = (osr.blockIndex || '').toString();
                                        if (!residentsByBlock[blockIndex]) {
                                          residentsByBlock[blockIndex] = [];
                                        }
                                        residentsByBlock[blockIndex].push(osr);
                                      });

                                      return (
                                        <tr key={`off-service-pgy-${pgyLevel}`} className="off-service-resident-row">
                                          <td className="ps-4 border-0">
                                            <span className="fw-bold">Off Service</span>
                                          </td>
                                          {visibleBlocks.map((block) => {
                                            const blockResidents = residentsByBlock[block.index.toString()] || [];
                                            
                                            // Group by major and minor rotations
                                            const majorResidents = blockResidents.filter(osr => {
                                              const hasMajor = !!(osr.majorRotation || (osr.rotations && osr.rotations.major));
                                              return hasMajor;
                                            });
                                            
                                            const minorResidents = blockResidents.filter(osr => {
                                              const hasMinor = !!(osr.minorRotation || (osr.rotations && osr.rotations.minor));
                                              return hasMinor;
                                            });

                                            const totalWeeks = majorWeeks + minorWeeks;

                                            return (
                                              <td
                                                key={`off-service-${pgyLevel}-${block.start}`}
                                                className="rotation-cell border-0"
                                                style={{ 
                                                  padding: '2px',
                                                  userSelect: 'auto'
                                                }}
                                              >
                                                <div style={{ display: 'flex', flexDirection: 'row', gap: '2px', minHeight: '50px', width: '100%' }}>
                                                  {/* Major rotation section */}
                                                  <div style={{ 
                                                    width: `${(majorWeeks / totalWeeks) * 100}%`,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '2px'
                                                  }}>
                                                    {majorResidents.length > 0 ? (
                                                      majorResidents.map((osr, idx) => {
                                                        const rotation = osr.majorRotation || (osr.rotations && osr.rotations.major) || '';
                                                        if (!rotation) return null;
                                                        const rotationColor = getRotationColor(rotation) || '#6B7280';
                                                        return (
                                                          <div 
                                                            key={idx}
                                                            className="custom-rotation-link-btn d-flex flex-row align-items-center justify-content-center gap-2"
                                                            style={{
                                                              backgroundColor: rotationColor,
                                                              color: 'white',
                                                              textAlign: 'center',
                                                              fontWeight: '600',
                                                              fontSize: '0.75rem',
                                                              minHeight: '50px',
                                                              borderRadius: '6px',
                                                              padding: '6px 4px',
                                                              width: '100%',
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              justifyContent: 'center',
                                                              flexDirection: 'column',
                                                              gap: '2px'
                                                            }}
                                                          >
                                                            <span>{abbreviateRotation(rotation)}</span>
                                                            <div className="badge py-1 px-2" style={{ fontSize: '0.65rem', backgroundColor: '#00102020'}}>
                                                              {osr.residentName}
                                                            </div>
                                                          </div>
                                                        );
                                                      })
                                                    ) : (
                                                      <div style={{ minHeight: '40px', width: '100%' }}></div>
                                                    )}
                                                  </div>
                                                  
                                                  {/* Minor rotation section */}
                                                  <div style={{ 
                                                    width: `${(minorWeeks / totalWeeks) * 100}%`,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '2px'
                                                  }}>
                                                    {minorResidents.length > 0 ? (
                                                      minorResidents.map((osr, idx) => {
                                                        const rotation = osr.minorRotation || (osr.rotations && osr.rotations.minor) || '';
                                                        if (!rotation) return null;
                                                        const rotationColor = getRotationColor(rotation) || '#6B7280';
                                                        return (
                                                          <div 
                                                            key={idx}
                                                            className="custom-rotation-link-btn d-flex flex-row align-items-center justify-content-center gap-2"
                                                            style={{
                                                              backgroundColor: rotationColor,
                                                              color: 'white',
                                                              textAlign: 'center',
                                                              fontWeight: '600',
                                                              fontSize: '0.75rem',
                                                              borderRadius: '6px',
                                                              padding: '6px 4px',
                                                              minHeight: '40px',
                                                              width: '100%',
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              justifyContent: 'center',
                                                              flexDirection: 'column',
                                                              gap: '2px'
                                                            }}
                                                          >
                                                            <span>{abbreviateRotation(rotation)}</span>
                                                            <div className="badge py-1 px-2" style={{ fontSize: '0.65rem', backgroundColor: '#00102020'}}>
                                                              {osr.residentName}
                                                            </div>
                                                          </div>
                                                        );
                                                      })
                                                    ) : (
                                                      <div style={{ minHeight: '40px', width: '100%' }}></div>
                                                    )}
                                                  </div>
                                                </div>
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      );
                                    })()}
                  </tbody>
                </Table>
                                </Col>
                                <Col md={2} sm={12} className="ps-md-0">
                                  <div 
                                    className="border-start no-border-start" 
                                    style={{ padding: '16px', backgroundColor: 'var(--bg-light)', height: '100%' }}
                                  >
                                    <h6 className="mb-3" style={{ fontSize: '0.9rem', fontWeight: '600' }}>Rotation Types:</h6>
                                    <div className="d-flex flex-row flex-wrap flex-md-column flex-md-nowrap gap-2">
                                      {Array.from(allRotations).map(rotation => {
                                        const config = scheduleData?.blockRotations?.[rotation];
                                        const columnType = config?.column || 'major';
                                        const totalWeeks = config?.totalWeeks || 0;
                                        
                                        return (
                                          <div 
                                            key={rotation} 
                                            className="d-flex align-items-center gap-2"
                                            style={{
                                              padding: '4px',
                                              borderRadius: '4px',
                                            }}
                                          >
                                            <div
                                              style={{
                                                width: '16px',
                                                height: '16px',
                                                backgroundColor: getRotationColor(rotation) || '#6B7280',
                                                borderRadius: '3px',
                                                flexShrink: 0
                                              }}
                                            />
                                            <div className="d-flex flex-column" style={{ lineHeight: '1.2' }}>
                                              <span className="small" style={{ fontSize: '0.8rem' }}>{abbreviateRotation(rotation)}</span>
                                              {totalWeeks > 0 && (
                                                <span className="text-muted" style={{ fontSize: '0.7rem' }}>{totalWeeks} week{totalWeeks !== 1 ? 's' : ''}</span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                </Card.Body>
              </Card>
                        );
                      })}
                    </>
                  );
                })()}
              </>
              )}
          </Tab.Pane>

          <Tab.Pane eventKey="weekend-call" active={activeTab === 'weekend-call'}>
            {Object.keys(schedules).length === 0 ? (
              <Card className="mb-4 shadow-sm border-0 py-4">
                <Card.Body className="p-0">
                  <div className="text-center py-5">
                    <div className="d-flex flex-column align-items-center gap-2">
                      <img src="/images/undraw_schedule_6t8k.svg" alt="No weekend call schedule" className="img-fluid" style={{height: "200px"}} />
                      <h5>No Weekend Call Schedule</h5>
                      <p className="text-muted">
                        No weekend call schedules have been created yet. Please check back later for updates.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <>

                {/* Vacation Card - Shows residents who are on vacation during the current month */}
                <Card className="mb-4 shadow-sm border-0">
                  <Card.Header className="fw-semibold d-flex border-0 justify-content-between align-items-center px-4 pt-4">
                     <h5 className="fw-semibold mb-0">
                      <span className="badge bg-secondary rounded-2 me-2"><i className="bi bi-calendar-check"></i></span>
                      Leave for the Month of {" "}
                      {(() => {
                        const months = getAllMonths(schedules);
                        const currentMonthStr = months && months[currentMonthIndex];
                        if (!currentMonthStr) return null;
                        const [displayYear, displayMonth] = currentMonthStr.split('-').map(str => parseInt(str, 10));
                        return new Date(displayYear, displayMonth - 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
                      })()}
                    </h5>
                    </Card.Header>
                  <Card.Body>

                    {(() => {
                      // Figure out the current month being displayed for weekend-call tab
                      const months = getAllMonths(schedules);
                      const currentMonthStr = months && months[currentMonthIndex];
                      if (!currentMonthStr) {
                        return <div className="text-muted">No schedule available.</div>;
                      }
                      const [displayYear, displayMonth] = currentMonthStr.split('-').map(str => parseInt(str, 10));

                      // Find residents with approved leave requests for the current month
                      const residentsOnVacation = [];
                      const monthStart = new Date(displayYear, displayMonth - 1, 1);
                      const monthEnd = new Date(displayYear, displayMonth, 0, 23, 59, 59);

                      // Filter approved vacation requests that overlap with the current month
                      const currentMonthRequests = approvedVacationRequests.filter(request => {
                        const requestStart = new Date(request.startDate);
                        const requestEnd = new Date(request.endDate);
                        
                        // Check if the request overlaps with the current month
                        return requestStart <= monthEnd && requestEnd >= monthStart;
                      });

                      // Group requests by resident and format the data
                      const residentsMap = new Map();
                      currentMonthRequests.forEach(request => {
                        const residentName = request.resident ? 
                          `${request.resident.firstName} ${request.resident.lastName}` : 
                          'Unknown Resident';
                        
                        if (!residentsMap.has(residentName)) {
                          residentsMap.set(residentName, []);
                        }
                        
                        // Format the date range - parse dates as local to avoid timezone issues
                        const startDate = new Date(request.startDate + 'T00:00:00');
                        const endDate = new Date(request.endDate + 'T00:00:00');
                        const startStr = startDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                        const endStr = endDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                        
                        residentsMap.get(residentName).push({
                          type: request.type,
                          dateRange: `${startStr} - ${endStr}`,
                          reason: request.reason || ''
                        });
                      });

                      // Convert map to array format and add rotation info
                      residentsMap.forEach((requests, residentName) => {
                        const timeframes = requests.map(req => 
                          `${req.type} (${req.dateRange})`
                        ).join(', ');
                        
                        // Find the resident's rotation for this month
                        let currentRotation = 'Unknown';
                        Object.values(schedules).forEach(pgyData => {
                          if (pgyData && pgyData.schedule) {
                            const isWeekly = pgyData.scheduleType === 'weekly';
                            const isBlocks = pgyData.scheduleType === 'blocks';
                            Object.entries(pgyData.schedule).forEach(([residentId, residentData]) => {
                              if (residentData.name === residentName && residentData.rotations) {
                                if (isWeekly || isBlocks) {
                                  // For weekly/block schedules, find rotation for any week in this month
                                  const [year, month] = currentMonthStr.split('-').map(Number);
                                  const monthEnd = new Date(year, month, 0);
                                  
                                  for (let day = 1; day <= monthEnd.getDate(); day++) {
                                    const checkDate = new Date(year, month - 1, day);
                                    const weekKey = getWeekStartForDate(checkDate);
                                    const weekRotation = residentData.rotations[weekKey];
                                    if (weekRotation) {
                                      currentRotation = weekRotation;
                                      break;
                                    }
                                  }
                                } else {
                                  // Monthly schedule
                                  if (residentData.rotations[currentMonthStr]) {
                                currentRotation = residentData.rotations[currentMonthStr];
                                  }
                                }
                              }
                            });
                          }
                        });
                        
                        residentsOnVacation.push({
                          name: residentName,
                          timeframe: timeframes,
                          rotation: currentRotation
                        });
                      });

                      if (residentsOnVacation.length === 0) {
                        return (
                          <div className="text-muted">
                            No approved leave requests this month.
                            <div className="mt-2 small">
                              <strong>Debug:</strong> {currentMonthStr} | Requests: {approvedVacationRequests.length}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div className="row">
                          {residentsOnVacation.map((data, idx) => (
                            <div key={idx} className="col-lg-4 col-md-6 col-sm-12 mb-2">
                              <div className="p-2 rounded bg-light">
                                <div className="d-flex flex-row justify-content-between align-items-center">
                                  
                                   <div className="d-flex flex-row align-items-center gap-2">
                                    
                                  <span 
                                    className="badge rounded-2" 
                                    style={{
                                      backgroundColor: getRotationColor(data.rotation),
                                      color: 'white',
                                      fontSize: '0.75em',
                                      fontWeight: '600'
                                    }}
                                  >
                                    {abbreviateRotation(data.rotation)}
                                  </span>
                                  <strong>{data.name}</strong>
                                </div>
                                   <span 
                                     className="text-primary fw-semibold" 
                                     style={{fontSize: "0.875em"}}
                                     dangerouslySetInnerHTML={{
                                       __html: data.timeframe.replace(/(Vacation|Sick Leave|Personal Leave|Conference|Other)/g, '<span class="text-muted">$1</span>')
                                     }}
                                   />
                                </div>
                               
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </Card.Body>
                </Card>
                <Card className="shadow-sm border-0 p-0">
                <Card.Header className="border-bottom fw-semibold d-flex justify-content-between align-items-center p-4" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                  <h5 className="fw-semibold">Weekend Call Schedule</h5>
    
                  <div className="d-flex align-items-center gap-3">
                    {/* Desktop month navigation */}
                    <div className="d-none d-md-flex align-items-center gap-3">
                      <ButtonGroup className="custom-btn-group">
                      <Button 
                        variant="secondary"
                        onClick={() => handleMonthNavigation('prev')}
                        disabled={currentMonthIndex === 0}
                        size="sm"
                      >
                        <i className="bi bi-chevron-left"></i>
                      </Button>
                      <Button disabled size="sm" variant="secondary">
                        {(() => {
                          const months = getAllMonths(schedules);
                          if (!months || months.length === 0 || !months[currentMonthIndex]) {
                            return 'No Schedule Available';
                          }
                          const [year, month] = months[currentMonthIndex].split('-');
                          return new Date(year, parseInt(month) - 1).toLocaleString('default', { 
                            month: 'long',
                            year: 'numeric'
                          });
                        })()}
                      </Button>
                      <Button 
                        variant="secondary"
                        onClick={() => handleMonthNavigation('next')}
                        disabled={currentMonthIndex >= getAllMonths(schedules).length - 1}
                        size="sm"
                      >
                        <i className="bi bi-chevron-right"></i>
                      </Button>
                      </ButtonGroup>
                    </div>
                    
                    {/* Mobile month dropdown */}
                    <div className="d-md-none">
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="light"
                          size="sm"
                          className="w-100"
                        >
                          {(() => {
                            const months = getAllMonths(schedules);
                            if (!months || months.length === 0 || !months[currentMonthIndex]) {
                              return 'No Schedule Available';
                            }
                            const [year, month] = months[currentMonthIndex].split('-');
                            return new Date(year, parseInt(month) - 1).toLocaleString('default', { 
                              month: 'long',
                              year: 'numeric'
                            });
                          })()}
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            width: '100%'
                          }}
                        >
                          {getAllMonths(schedules).map((monthStr, index) => {
                            const [year, month] = monthStr.split('-');
                            return (
                              <Dropdown.Item
                                key={monthStr}
                                onClick={() => setCurrentMonthIndex(index)}
                                active={index === currentMonthIndex}
                                style={{
                                  padding: '8px 16px',
                                  cursor: 'pointer'
                                }}
                              >
                                {new Date(year, parseInt(month) - 1).toLocaleString('default', {
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </Dropdown.Item>
                            );
                          })}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                <Table responsive striped className="custom-border">
                  <thead>
                    <tr className="custom-header-bg">
                      <th></th>
                      {getWeekendsForMonth(getAllMonths(schedules)[currentMonthIndex]).map(weekend => (
                        <th key={weekend.saturday} colSpan="2" className="text-center">
                          {(() => {
                            const [year, month, day] = weekend.saturday.split("-").map(Number);
                            const saturdayDate = new Date(year, month - 1, day);
                            return saturdayDate.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            });
                          })()}
                          -{" "}
                          {(() => {
                            const [year, month, day] = weekend.sunday.split("-").map(Number);
                            const sundayDate = new Date(year, month - 1, day);
                            return sundayDate.toLocaleDateString(undefined, {
                              day: "numeric",
                            });
                          })()}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      <th className="ps-4">Rotations</th>
                      {getWeekendsForMonth(getAllMonths(schedules)[currentMonthIndex]).map(weekend => (
                        <React.Fragment key={weekend.saturday}>
                          <th>Sat</th>
                          <th>Sun</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(getCombinedWeekendSchedules()).map(([rotation, weekendData]) => (
                      <tr key={rotation}>
                        <td className="ps-4">
                          <span 
                            className="d-none d-md-inline text-nowrap"
                            style={{
                              backgroundColor: getRotationColor(rotation),
                              color: 'white',
                              padding: '6px 8px',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              width: '100%',
                              textAlign: 'center',
                              display: 'inline-block',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal'
                            }}
                          >
                            {abbreviateRotation(rotation)}
                          </span>
                          <span 
                            className="d-inline d-md-none"
                            style={{
                              backgroundColor: getRotationColor(rotation),
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '0.875rem',
                              fontWeight: '600'
                            }}
                          >
                            {getRotationLabel(rotation)}
                          </span>
                        </td>
                        {getWeekendsForMonth(getAllMonths(schedules)[currentMonthIndex]).map(weekend => (
                          <React.Fragment key={weekend.saturday}>
                            <td style={{ minHeight: '100px' }}>
                              {weekendData[weekend.saturday]?.residents.map((resident, index) => (
                                <div
                                  key={`${resident.id}-${weekend.saturday}-${index}`}
                                  className="p-2 m-1"
                                >
                                  {renderResidentName(resident.id, `${resident.name} (PGY-${resident.year})`)}
                                </div>
                              ))}
                            </td>
                            <td style={{ minHeight: '100px' }}>
                              {weekendData[weekend.sunday]?.residents.map((resident, index) => (
                                <div
                                  key={`${resident.id}-${weekend.sunday}-${index}`}
                                  className="p-2 m-1"
                                >
                                  {renderResidentName(resident.id, `${resident.name} (PGY-${resident.year})`)}
                                </div>
                              ))}
                            </td>
                          </React.Fragment>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </Card.Body>
                </Card>
              </>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="daily-schedule" active={activeTab === 'daily-schedule'}>
            {Object.keys(schedules).length === 0 ? (
              <Card className="mb-4 shadow-sm border-0 py-4">
                <Card.Body className="p-0">
                  <div className="text-center py-5">
                    <div className="d-flex flex-column align-items-center gap-2">
                      <img src="/images/undraw_schedule_6t8k.svg" alt="No daily schedule" className="img-fluid" style={{height: "200px"}} />
                      <h5>No Daily Schedule</h5>
                      <p className="text-muted">
                        No daily schedules have been created yet. Please check back later for updates.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              renderDailySchedule()
            )}
          </Tab.Pane>
        </Tab.Content>
      </Container>
    </div>
  );
};

export default ResidentRotationSchedulerPage; 